import React from 'react';
import { ShieldCheck, Award, Info, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { localeForCountry } from '../utils/locale';

/**
 * Listing trust banner — honest version.
 *
 * IMPORTANT: this component used to display "Photos verified", "Measurements
 * verified" and "BER verified" badges with a fake cryptographic hash on
 * every listing. None of those claims were substantiated by any backend
 * check. Showing them risked breaching:
 *   • Irish Consumer Protection Act 2007 (misleading commercial practices)
 *   • PSRA accuracy-of-representation rules
 *   • SEAI BER register integrity expectations
 *
 * What this now shows is **only** what we can truthfully claim today:
 *   • Listed-on date (from createdAt — true)
 *   • Reviewed by lovinahome moderation (we manually review listings; true
 *     once the moderation flow is wired — until then we don't claim it)
 *   • BER status: shows "Pending verification" by default. Only flips to
 *     "BER verified" when `property.ber_verified === true` (set by the
 *     forthcoming SEAI register cross-check, Phase B3).
 *
 * The old `VerificationBadgeCompact` no-arg pill is preserved as a NAMED
 * EXPORT for backwards compatibility but now renders nothing — it's
 * intentionally a no-op so callers don't break, and we avoid silently
 * re-introducing the misleading green dot anywhere on the site.
 */

// Backwards-compatible export — INTENTIONALLY renders nothing.
// Old name kept so we don't blow up imports during the cleanup pass.
export const VerificationBadgeCompact = () => null;

const formatDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('en-IE', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return null;
  }
};

const VerificationBadges = ({ property }) => {
  const L = localeForCountry(property?.location?.country);
  const listedDate = formatDate(property?.createdAt || property?.dateAdded);
  // Real cert verification flag — only true once we've matched the
  // listing to the BER (IE) / EPC (UK) register. Default: pending.
  const certVerified = property?.ber_verified === true || property?.epc_verified === true;

  return (
    <Card data-testid="listing-trust-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          Listing details
        </CardTitle>
        <CardDescription className="text-xs">
          What we know about this listing today. We're upfront about what's
          checked and what isn't —{' '}
          <Link to="/how-we-vet-listings" className="underline hover:text-[#7042cc]">
            how we vet listings
          </Link>
          .
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Listed date — always true */}
          <div
            className="rounded-lg border border-gray-200 bg-white p-3 flex items-start gap-2"
            data-testid="trust-listed-on"
          >
            <Calendar className="h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-gray-900">Listed on</div>
              <div className="text-xs text-gray-600 mt-0.5">
                {listedDate || 'Recently listed'}
              </div>
            </div>
          </div>

          {/* Cert status — honest pending vs verified, locale-aware */}
          <div
            className={`rounded-lg border p-3 flex items-start gap-2 ${
              certVerified
                ? 'border-emerald-200 bg-emerald-50/50'
                : 'border-gray-200 bg-white'
            }`}
            data-testid="trust-ber-status"
          >
            <Award className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
              certVerified ? 'text-emerald-700' : 'text-gray-500'
            }`} />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-gray-900">
                {L.energyLabel} {property?.ber || '—'}
                {certVerified && (
                  <span className="ml-1.5 text-[10px] uppercase tracking-wider text-emerald-700 font-bold">
                    Verified
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-600 mt-0.5">
                {certVerified
                  ? `Matched to the official ${L.energyRegisterName}.`
                  : `Self-declared by the lister. Not yet cross-checked against the ${L.energyRegisterName}.`}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-md bg-gray-50 border border-gray-200 p-2.5 flex items-start gap-2">
          <Info className="h-3.5 w-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-gray-600 leading-relaxed">
            <strong>Photos and measurements are supplied by the lister.</strong>{' '}
            Always inspect a property in person and request the {L.energyLabel} cert
            directly before making any offer. {L.energyRegisterName} cross-check, surveyor-confirmed
            measurements and independently-captured photos are coming soon for
            premium listings.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default VerificationBadges;
